import { Badge, Button, Card, DataTable, Link, Page } from '@shopify/polaris';
import Pagination from 'components/pagination';
import __helpers from 'helpers/index';
import {
  ENUM_TRIP_MANIFEST_STATUS,
  tripStatusLabels,
  TypedTripManifest,
  useDownloadCurrentTripManifest,
  useGetTrip_manifests_list,
} from 'queries/trip_manifest.query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import TripmanifestFilter from './filter';
import { MarketsIcon, PageDownIcon } from '@shopify/polaris-icons';
import { useMutateCreateDriverSettlement } from 'queries/driver_settlement.query';
import { useAuth } from 'context/AuthContext';

dayjs.extend(utc);
dayjs.extend(timezone);

function getTripStatusTone(status?: number) {
  switch (status) {
    case ENUM_TRIP_MANIFEST_STATUS.DISPATCHED:
      return 'info' as const;
    case ENUM_TRIP_MANIFEST_STATUS.IN_TRANSIT:
      return 'success' as const;
    case ENUM_TRIP_MANIFEST_STATUS.COMPLETED:
      return 'success' as const;

    case ENUM_TRIP_MANIFEST_STATUS.FINALIZED:
      return 'success-strong' as const;
    case ENUM_TRIP_MANIFEST_STATUS.CANCELLED:
      return 'critical' as const;
    case ENUM_TRIP_MANIFEST_STATUS.DRAFT:
    default:
      return 'attention' as const;
  }
}

/**
    <Badge tone={getTripStatusTone(manifest.trip_status)}>
        {tripStatusLabels[manifest.trip_status as ENUM_TRIP_MANIFEST_STATUS] || 'Không xác định'}
    </Badge>
 */

export default function TripManifest() {
  const navigate = useNavigate();
  const [entities, setEntities] = useState<TypedTripManifest[]>([]);
  const [totalItems, setTotalItems] = useState(0);

  const { currentUserCan } = useAuth();

  /**
   * If user apply filter, it will add to URL, then parse URL back to initial state
   */
  let { search } = useLocation();
  let StringQuery: any = __helpers.ExtractUrl(search);

  const initialQuery = {
    query: '',
    page: 1,
    limit: 100,
    sort: 'departure_date:desc',
  };
  const [mainQuery, setMainQuery] = useState({
    ...initialQuery,
    ...StringQuery,
  });

  const { data, refetch: getEntities, isPending: loading } = useGetTrip_manifests_list(mainQuery);

  useEffect(() => {
    if (data) {
      setEntities(data.body);
      setTotalItems(data.totalItems);
    }
  }, [data]);

  /**
   * Change page number
   */
  const onChangePagination = useCallback(
    (numPage: number, limit: number) => {
      setMainQuery({ ...mainQuery, page: numPage, limit: limit });
    },
    [mainQuery]
  );

  const reduceRequest = useCallback((mainQuery) => {
    getEntities();
    return mainQuery;
  }, []);

  const reduceRequestMemo = useMemo(() => {
    return __helpers.debounce((_value) => {
      reduceRequest?.call(this, _value);
    }, 500);
  }, []);

  /**
   * I do not know ...
   * Bug: In React, do NOT remove this code
   */
  const filterCallback = useCallback((_value: any) => {
    if (_value === false) {
      return setMainQuery(initialQuery);
    } else {
      setMainQuery((oldValue) => {
        return { ...oldValue, ..._value };
      });
    }
  }, []);

  /**
   * Double request!
   */
  useEffect(() => {
    let buildURLSearch = __helpers.buildEndUrl(mainQuery);
    if (search !== buildURLSearch) navigate(`/trip_manifest` + buildURLSearch);
    reduceRequestMemo(mainQuery);
  }, [mainQuery]);

  /** Tính tổng trong bảng  */
  let [totalInTable, setTotalInTable] = useState({
    total_fee: 0,
    total_fare: 0,
    total_due: 0,
    total_raw_due: 0,
  });

  useEffect(() => {
    if (!entities) return;

    let total = {
      total_fee: 0,
      total_fare: 0,
      total_due: 0,
      total_raw_due: 0,
    };

    for (let tripManifest of entities) {
      total.total_raw_due += Number(tripManifest.total_amount_due);
      total.total_due += Number(tripManifest.total_amount_due) - Number(tripManifest.total_fee || 0);
      total.total_fare += Number(tripManifest.total_fare);
      total.total_fee += Number(tripManifest.total_fee);
    }

    setTotalInTable(total);
  }, [entities]);

  /** đánh dấu tạo quyết toán ... */
  const [markingID, setMarkingID] = useState(null);
  const { mutateAsync: createDriverSettlement, isPending: creatingDriverSettlement } = useMutateCreateDriverSettlement();
  const markAsDoneCallback = useCallback(async (trip_manifest_id) => {
    setMarkingID(trip_manifest_id);
    try {
      await createDriverSettlement(trip_manifest_id);
    } catch (e) {}
    setMarkingID(null);
  }, []);

  /** Xuất excel */
  const { mutateAsync: downloadExcelThisFilter, isPending: downloadGenerating } = useDownloadCurrentTripManifest();
  const generateDownloadLink = useCallback(async () => {
    try {
      const blob = await downloadExcelThisFilter(mainQuery);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.href = url;
      a.download = `trip_manifest-${dayjs().format('YYYY-MM-DD')}.xlsx`;

      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (e) {}
  }, [mainQuery]);

  return (
    <>
      <Helmet>
        <title>Danh sách chuyến đi</title>
      </Helmet>

      <Page
        fullWidth
        title="Danh sách chuyến đi"
        primaryAction={{
          icon: PageDownIcon,
          content: 'Tải xuống bảng này',
          onAction: () => generateDownloadLink(),
          disabled: !currentUserCan('download_trip_manifest'),
        }}
      >
        <Card padding="0">
          <TripmanifestFilter onCallback={filterCallback} loading={loading} />
          <DataTable
            hideScrollIndicator
            columnContentTypes={['text']}
            headings={[
              'ID',
              'Lái xe',
              'Hành trình',
              'Xe',
              'Ngày đi',
              'Kết thúc',
              'Tình trạng chuyến',
              'Tổng tiền vé',
              'Tổng tiền phí',
              'Tài xế thu',
              'Số ghế',
              'Số hàng',
              'Quyết toán',
              'Tạo lúc',
              '--',
            ]}
            rows={
              entities
                ? entities.map((tripManifest) => {
                    // phục vụ tạo link
                    let trip_date_reformat = dayjs(Number(tripManifest.departure_date)).tz('Asia/Ho_Chi_Minh').format('YYYY-MM-DD');
                    let mainDriver = tripManifest.trip_manifest_driver.map((d) => {
                      return `${d.driver_data.display_name} (${d.driver_role})`;
                    });
                    return [
                      tripManifest.id,
                      String(mainDriver),
                      <Link
                        url={`/ticket_managers?d=${trip_date_reformat}&journey_id=${tripManifest?.trip?.journey?.id}&h=${tripManifest.time_id}`}
                        removeUnderline
                      >
                        <strong>{tripManifest?.trip?.journey?.journey_name}</strong>
                      </Link>,

                      tripManifest?.vehicle?.license_plate,
                      tripManifest.departure_date
                        ? dayjs(Number(tripManifest.departure_date)).tz('Asia/Ho_Chi_Minh').format('HH:mm DD-MM-YYYY')
                        : '--',
                      tripManifest.arrivedOn ? dayjs(Number(tripManifest.arrivedOn)).tz('Asia/Ho_Chi_Minh').format('HH:mm DD-MM-YYYY') : '--',
                      <Badge tone={getTripStatusTone(tripManifest.trip_status)}>
                        {tripStatusLabels[tripManifest.trip_status as ENUM_TRIP_MANIFEST_STATUS] || 'Không xác định'}
                      </Badge>,
                      __helpers.formatNumberWithCommas(tripManifest.total_fare),
                      __helpers.formatNumberWithCommas(tripManifest.total_fee || 0),
                      __helpers.formatNumberWithCommas(tripManifest.total_amount_due),
                      tripManifest.total_occupied_seats || 0,
                      tripManifest.total_occupied_goods || 0,
                      __helpers.formatNumberWithCommas(Number(tripManifest.total_amount_due) - Number(tripManifest.total_fee || 0)),
                      dayjs(Number(tripManifest.createdAt)).tz('Asia/Ho_Chi_Minh').format('HH:mm DD-MM-YYYY'),

                      tripManifest.trip_status === ENUM_TRIP_MANIFEST_STATUS.FINALIZED ? (
                        tripManifest.driver_settlement ? null : (
                          <Button
                            variant="monochromePlain"
                            icon={MarketsIcon}
                            loading={creatingDriverSettlement && markingID === tripManifest.id}
                            onClick={() => markAsDoneCallback(tripManifest.id)}
                          >
                            Tạo quyết toán
                          </Button>
                        )
                      ) : null,
                    ];
                  })
                : []
            }
            totals={[
              '',
              `${totalItems} chuyến`,
              '-',
              '-',
              '-',
              '-',
              '-',
              __helpers.formatNumberWithCommas(totalInTable.total_fare),
              __helpers.formatNumberWithCommas(totalInTable.total_fee),
              __helpers.formatNumberWithCommas(totalInTable.total_raw_due),
              '-',
              '-',
              __helpers.formatNumberWithCommas(totalInTable.total_due),
              '-',
              '-',
            ]}
            showTotalsInFooter
            totalsName={{ singular: 'Tổng', plural: 'Tổng' }}
            hasZebraStripingOnData
            increasedTableDensity
          />
        </Card>
        <br />

        {totalItems > mainQuery.limit ? (
          <Pagination TotalRecord={totalItems} activeCurrentPage={mainQuery.page} pageSize={mainQuery.limit} onChange={onChangePagination} />
        ) : null}
      </Page>

      <br />
      <br />
      <br />
    </>
  );
}
